import { Modal, Slider } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import React, { useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { canvasPreview } from "./canvasPreview";
import { useDebounceEffect } from "./useDebounceEffect";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: 600,
  bgcolor: "background.paper",
  overflow: "auto",
  boxShadow: 24,
  p: 4,
  outline: "none",
  borderRadius: "0.5rem",
};

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

function PictureCropper({
  imgSrc,
  open,
  aspect = 1,
  handleAfterCrop,
  handleCancel,
  onClose = () => {},
}) {
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const hiddenAnchorRef = useRef(null);
  // const blobUrlRef = useRef("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [scale, setScale] = useState(1);
  const [rotate] = useState(0);

  function onImageLoad(e) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
      setScale(1);
    }
  }

  const resetStates = () => {
    setScale(1);
    setCrop();
    setCompletedCrop();
  };

  async function onDownloadCropClick() {
    previewCanvasRef.current.toBlob(
      (blob) => {
        if (!blob) {
          throw new Error("Failed to create blob");
        }
        handleAfterCrop(blob);
        resetStates();
        // if (blobUrlRef.current) {
        //   URL.revokeObjectURL(blobUrlRef.current);
        // }
        // blobUrlRef.current = URL.createObjectURL(blob);
        // hiddenAnchorRef.current.href = blobUrlRef.current;
        // hiddenAnchorRef.current.click();
      },
      "image/jpeg",
      0.5
    );
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  const onCancelButtonClick = () => {
    handleCancel();
    resetStates();
  };
  return (
    <Modal
      open={open}
      onClose={() => {
        resetStates();
        onClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <MDTypography>Crop image</MDTypography>
        {!!imgSrc && (
          <>
            <MDBox
              sx={{
                overflow: "auto",
                flexGrow: 1,
                mb: 2,
                maxHeight: "calc(90vh - 150px)", // Adjust based on your modal padding and button height
              }}
            >
              <ReactCrop
                style={{
                  right: "-50%",
                  transform: `translate(-50%, 0px)`,
                }}
                crop={crop}
                onChange={(_, percentCrop) => {
                  setCrop(percentCrop);
                }}
                onComplete={(c) => {
                  setCompletedCrop(c);
                }}
                aspect={aspect}
                circularCrop
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  style={{
                    transform: `scale(${scale}) rotate(${rotate}deg)`,
                    // height: "44vh",
                    maxHeight: style.maxHeight,
                    width: "100%",
                  }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </MDBox>
            <Slider
              value={scale}
              min={1}
              max={3}
              step={0.001}
              onChange={(event, newZoom) => setScale(newZoom)}
              aria-labelledby="image-zoom-slider"
            />
          </>
        )}

        {!!completedCrop && (
          <MDBox sx={{ display: "none" }}>
            <div>
              <canvas
                ref={previewCanvasRef}
                style={{
                  border: "1px solid black",
                  objectFit: "contain",
                  width: completedCrop.width,
                  height: completedCrop.height,
                }}
              />
            </div>
            <div>
              <a
                href="/s"
                ref={hiddenAnchorRef}
                download
                style={{
                  position: "absolute",
                  top: "-200vh",
                  visibility: "hidden",
                }}
              >
                Hidden download
              </a>
            </div>
          </MDBox>
        )}
        <MDBox display="flex" justifyContent="end" sx={{ gap: "1rem", mt: 2 }}>
          <MDButton variant="gradient" color="secondary" onClick={() => onCancelButtonClick()}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="newColor" onClick={() => onDownloadCropClick()}>
            Crop image
          </MDButton>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default PictureCropper;
